import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';

// T.Z113 — the ONE fold of the two «Провалил ЕВИ» facts (auto + manual). The web
// imports the runtime helper (not just types) because the chip must decide its label
// by source, and two surfaces inventing the same fold is how they drift.
import { eviFailedChip } from '@cb-manager/shared';

import type { ApplicationDetailDto, ApplicationPatchInput } from '../../api/applications';
import { useApplicationDetail, usePatchApplication } from '../../api/applications';
import {
  useApplicationManagerStatuses,
  useManagerStatusDictionary,
  useSetApplicationManagerStatuses,
} from '../../api/manager-statuses';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { hasAtLeast } from '../../auth/roles';
import { Combobox } from '../../components/Combobox';
import { Badge, EmptyState, ErrorState, Input, Spinner } from '../../components/ui';
import { formatDate, formatDateTime, MISSING } from '../dashboard/format';
import {
  NO_STATUS_LABEL,
  managerStatusBlock,
  managerStatusErrorText,
  managerStatusPut,
} from './manager-statuses-model';
import {
  VENUE_ASSIGN_CONFIRM_LABEL,
  VENUE_ASSIGN_LABEL,
  VENUE_EMPTY_LABEL,
  VENUE_OFFLINE_FIRST_HINT,
  VENUE_OFFLINE_HINT,
  VENUE_REMOVE_CONFIRM_LABEL,
  VENUE_REMOVE_LABEL,
  VENUE_ROLE_HINT,
  emptyPiiFillDraft,
  flagTogglePatch,
  piiFillPatch,
  piiFillState,
  venueAssignError,
  venueAssignFallbackOption,
  venueAssignOptions,
  venueAssignPatch,
  venueControlState,
  venueRemovePatch,
  type FlagField,
  type PiiFillDraft,
  type VenueConfirmIntent,
} from './applications-form';
import { useVenueDirectory } from '../quotas/dictionary';
import {
  CONTRACT_DEADLINE_CHIP_TITLE,
  CONTRACT_STATE_LABEL,
  ENROLL_STATE_LABEL,
  EVI_DEADLINE_DAYS,
  FIELD_LABEL,
  FIELD_SOURCE,
  PARAM,
  SOURCE_LABEL,
  VENUE_MATCH_LABEL,
  boolLabel,
  contractDeadlineView,
  courseLabel,
  displayStatusView,
  enrolledView,
  eviDeadline,
  formatDateOnly,
  legalRepConsentView,
  parsePguId,
  signerMatchesView,
  signerNameLine,
  profileFieldGroups,
  snapshotAddressView,
  timelineOf,
  SNAPSHOT_ADDRESS_LABEL,
  type FieldSource,
} from './applications-model';

import './applications.css';

/**
 * Application profile (И4, task T.C3).
 *
 * Two mounts, one component:
 *   * the drawer on `/applications?pguId=…` (the register's own URL, so closing it
 *     restores the filtered list), and
 *   * the full page on `/applications/:pguId`, which the router mounts with the
 *     path parameter.
 *
 * The PII is rendered as the api sent it (T.R2: clear text to every role); the
 * manual repairs (flags, venue override, PII null-fill) are still MANAGER+ only
 * (`hasAtLeast(role, 'MANAGER')`) — the server stays the authority, the UI only
 * avoids rendering controls a viewer could not use.
 */
export function ApplicationProfilePage() {
  const routeParams = useParams();
  const [searchParams] = useSearchParams();
  // The route param wins; the query param keeps the drawer mount working.
  const pguId = parsePguId(routeParams.pguId ?? searchParams.get(PARAM.PGU_ID) ?? undefined);

  return (
    <>
      <div className="page-header">
        <h1>Заявление</h1>
        <p className="page-header__sub">Профиль заявления, источники полей и история изменений.</p>
      </div>
      <div className="card">
        <ApplicationProfile pguId={pguId} />
      </div>
    </>
  );
}

export function ApplicationProfile({
  pguId,
  onBack,
}: {
  pguId: string | null;
  /** Drawer mount: closes the panel and restores the register's filters. */
  onBack?: () => void;
}) {
  if (pguId === null) {
    return (
      <ErrorState
        message="ЕПГУ ID обязателен и должен быть строкой из 10 цифр, начинающейся с 7."
      />
    );
  }
  return <ApplicationProfileLoaded key={pguId} pguId={pguId} onBack={onBack} />;
}

/**
 * ADDITIVE (v1.9.21, T.Z101) — the contract chip of the card header: the same fold
 * the register cell shows, with the long wording and the signing day. `null` renders
 * «Договор: —» — the sync has not spoken about the row, and «не подписан» would be a
 * guess, not a fact.
 */
function ContractChip({ state, signedAt }: { state: ApplicationDetailDto['contractState']; signedAt: string | null }) {
  if (state === null) {
    return (
      <span
        className="cell-muted"
        data-testid="profile-contract"
        data-contract="none"
        title="Синк договоров ещё не отмечал это заявление"
      >
        Договор: —
      </span>
    );
  }
  return (
    <span
      className="source-badge"
      data-testid="profile-contract"
      data-contract={state}
      title={state === 'SIGNED' && signedAt ? `Подписан ${signedAt}` : CONTRACT_STATE_LABEL[state]}
    >
      Договор: {CONTRACT_STATE_LABEL[state]}
      {state === 'SIGNED' && signedAt ? ` ${formatDateOnly(signedAt)}` : ''}
    </span>
  );
}

/**
 * ADDITIVE (v1.9.24, T.Z111) — the enrollment chip of the card header: the enrollment
 * twin of {@link ContractChip}, same `source-badge` chrome, red/green through the tone
 * classes the badge pair uses. No `enrollState` renders nothing — the card must not
 * claim «не зачислен» for a row the sync has said nothing about.
 */
function EnrollChip({ state }: { state: NonNullable<ApplicationDetailDto['enrollState']> }) {
  return (
    <span className="source-badge" data-testid="profile-enroll" data-enroll={state} title={ENROLL_STATE_LABEL[state]}>
      Зачисление: {ENROLL_STATE_LABEL[state]}
    </span>
  );
}

/**
 * ADDITIVE (v1.9.46, T.Z165; amended v1.9.59, T.Z187) — the «Дедлайн договора» line of
 * the card header, the SAME view the register's column renders, sitting next to the
 * {@link ContractChip} block: the clock a manager chases — the УНТИ auto-decline date
 * (fallback pass+21), the rule named in the `title`. Renders ONLY while the clock
 * runs — a signed row's {@link ContractChip} already carries the signing day (a second
 * «подписан» line would say the same fact twice), and a row neither source dates has
 * nothing to count from, which the quiet absence of the line says better than a dash.
 */
function ContractDeadlineChip({ application }: { application: ApplicationDetailDto }) {
  const view = contractDeadlineView(application);
  if (view === null || view.tone === 'signed') return null;
  return (
    <span
      className="source-badge"
      data-testid="profile-contract-deadline"
      data-deadline={view.tone}
      title={CONTRACT_DEADLINE_CHIP_TITLE}
    >
      Дедлайн договора: {view.label}
    </span>
  );
}

/**
 * ADDITIVE (v1.9.49, T.Z169) — the «Получено согласие родителей» line of the card
 * header, sitting in the Договор block right after the contract/deadline chips: the
 * applicant's own checkbox on the iSmart `future` platform, copied by the same
 * worker sync (migration 0040). Three honest states, exactly the ones the column
 * stores — {@link legalRepConsentView} owns the fold (✓ calm green / ✗ «Не
 * отмечено» with the danger ink only when the contract is already signed — the
 * Владимир-Костин case the owner asked the screen for / «—» when the sync has no
 * live platform row for the child, which is never a «не отмечено» answer).
 */
function LegalRepConsentChip({ application }: { application: ApplicationDetailDto }) {
  const view = legalRepConsentView(application);
  return (
    <span
      className={
        view.tone === 'ok'
          ? 'source-badge source-badge--ok'
          : view.tone === 'bad'
            ? 'source-badge source-badge--bad'
            : 'source-badge'
      }
      data-testid="profile-legal-rep-consent"
      data-consent={application.legalRepConsent === null || application.legalRepConsent === undefined
        ? 'none'
        : String(application.legalRepConsent)}
      title={view.title}
    >
      Получено согласие родителей: {view.mark ? `${view.mark} ` : ''}
      {view.label}
    </span>
  );
}

/**
 * ADDITIVE (v1.9.51, T.Z172) — the «Договор заключен ПП?» line of the card header,
 * sitting in the Договор block right after the consent chip: does the person who
 * pressed «Подписать» in iSmart fold to the child we hold (migration 0041). Three
 * honest states, exactly the ones the column stores — {@link signerMatchesView} owns
 * the fold (✓ «Да» calm green / ✗ «Нет» in the DANGER ink the owner's CSV-audit case
 * asked for — 12 of 2 810 signed contracts signed by somebody else / «—» when there is
 * no verdict: no signer, no our name, or the contract unsigned, which is never a
 * «нет» answer).
 */
function SignerMatchesChip({ application }: { application: ApplicationDetailDto }) {
  const view = signerMatchesView(application);
  return (
    <span
      className={
        view.tone === 'ok'
          ? 'source-badge source-badge--ok'
          : view.tone === 'bad'
            ? 'source-badge source-badge--bad'
            : 'source-badge'
      }
      data-testid="profile-signer-matches"
      data-signer={application.signerMatches === null || application.signerMatches === undefined
        ? 'none'
        : String(application.signerMatches)}
      title={view.title}
    >
      Договор заключен ПП?: {view.mark ? `${view.mark} ` : ''}
      {view.label}
    </span>
  );
}

/**
 * ADDITIVE (v1.9.52, T.Z176) — the «Зачислен» line of the card header, the
 * cat.2035.university mirror's own word (the unti2035 sweep's fact, migration
 * 0043): ✓ «Да» in the calm ink when their `status` folds to `accepted` (the
 * owner's one-line rule: Да = ACCEPTED, case-insensitive, нет = every other
 * literal), «Нет» in the NEUTRAL ink (a live-mirror fact about their vocabulary,
 * not an alarm — the funnel status badge beside it stays the authority), and «—»
 * when the sweep has not covered the row, which is never a «нет» answer. Three
 * honest states, exactly the ones the column stores — {@link enrolledView} owns
 * the fold. Deliberately NOT the «Зачислен» of the contract axis
 * (`application.enrollState`): that chip reads the iSmart platform's own word, and
 * the two systems can disagree — each names its source in the tooltip.
 */
function EnrolledChip({ application }: { application: ApplicationDetailDto }) {
  const view = enrolledView(application);
  return (
    <span
      className={view.tone === 'ok' ? 'source-badge source-badge--ok' : 'source-badge'}
      data-testid="profile-enrolled"
      data-enrolled={application.enrolled === null || application.enrolled === undefined ? 'none' : String(application.enrolled)}
      title={view.title}
    >
      Зачислен: {view.mark ? `${view.mark} ` : ''}
      {view.label}
    </span>
  );
}

/**
 * ADDITIVE (v1.9.51, T.Z172) — the signer's identity line UNDER the verdict chip:
 * «Подписал (по данным CAT): <имя>», rendered ONLY when the sync brought a name (the
 * name is staff-visible data, same as the rest of the drawer — it is PII stored on
 * the row by owner decision 23.09, and the drawer is the one surface that renders it).
 */
function SignerNameChip({ application }: { application: ApplicationDetailDto }) {
  const line = signerNameLine(application.signerName);
  if (line === null) return null;
  return (
    <span className="source-badge" data-testid="profile-signer-name" title="Кто подписал договор на платформе iSmart">
      {line}
    </span>
  );
}

function ApplicationProfileLoaded({ pguId, onBack }: { pguId: string; onBack?: () => void }) {
  const { role } = useAuth();
  const canRepair = hasAtLeast(role, 'MANAGER');
  const detail = useApplicationDetail(pguId);
  const patch = usePatchApplication(pguId);

  if (detail.isPending) return <Spinner label="Загружаем заявление" />;

  if (detail.isError) {
    return (
      <ErrorState
        message={detail.error instanceof Error ? detail.error.message : 'Не удалось загрузить заявление.'}
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const application = detail.data.data;
  // T.Z143 (v1.9.36) — the card's status badge reads the DISPLAY status: «Провалил
  // ЕВИ» when the fact holds, the stored status otherwise — the same fold the
  // register's badge and the `?status=EVI_FAILED` filter speak.
  const statusView = displayStatusView(application);

  return (
    <div className="profile" data-testid="application-profile" data-pgu-id={pguId}>
      {onBack ? (
        <div>
          <button type="button" className="btn btn--ghost btn--sm" data-testid="profile-back" onClick={onBack}>
            ← К списку
          </button>
        </div>
      ) : null}

      <header className="profile__head">
        <span data-testid="profile-status" data-status={statusView.status}>
          <Badge tone={statusView.tone} title={statusView.title}>
            {statusView.label}
          </Badge>
        </span>
        <span className="profile__title" data-testid="profile-pgu-id">
          {application.pguId ?? '—'}
        </span>
        <EviDeadlineChip application={application} />
        <FlagBadge label="органик" on={application.isOrganic} testId="profile-flag-organic" />
        <FlagBadge label="отказ от договора" on={application.agreementRejected} testId="profile-flag-rejected" />
        {/* ADDITIVE (v1.9.25, T.Z113) — «Провалил ЕВИ» as ONE chip for either source:
            the AUTO fact (the worker's registry engine: the child vanished from the
            fresh ЕВИ pass list with no FTP evidence — since v1.9.25) and the MANUAL
            one (T.Z96, a manager's own verdict) fold through the shared
            `eviFailedChip`, so the card can never paint two chips for one fact. The
            caption still says «(вручную)» when the human is the only source; the chip
            never replaces the derived status badge. */}
        <EviFailedChip
          eviFailed={application.eviFailed}
          eviFailedManually={application.eviFailedManually}
        />
        {/* ADDITIVE (v1.9.21, T.Z101) — the contract fact as its OWN chip: it names the
            platform's answer and never replaces the derived status badge. */}
        <ContractChip state={application.contractState} signedAt={application.contractSignedAt} />
        {/* ADDITIVE (v1.9.46, T.Z165) — the clock of the fact above it, the same view
            the register's column renders. Nothing extra for a signed row (the chip
            above carries the day) and nothing at all without a pass fact. */}
        <ContractDeadlineChip application={application} />
        {/* ADDITIVE (v1.9.49, T.Z169) — «Получено согласие родителей», the third fact
            axis of the same sync, read in the Договор block after the contract line:
            the checkbox the owner's Владимир-Костин case turns on (signed contract +
            no consent = the danger ink). */}
        <LegalRepConsentChip application={application} />
        {/* ADDITIVE (v1.9.51, T.Z172) — «Договор заключен ПП?», the fourth fact axis
            of the same sync, read in the Договор block right after the consent line:
            «Да» calm / «Нет» danger, and the signer's identity line under it (rendered
            only when the sync brought a name). */}
        <SignerMatchesChip application={application} />
        <SignerNameChip application={application} />
        {/* ADDITIVE (v1.9.52, T.Z176) — «Зачислен», the cat.2035 mirror's word, the
            fifth fact axis beside the row: Да calm / Нет neutral / «—», rendered
            always (the honest «—» is the not-covered answer, never a guessed no). */}
        <EnrolledChip application={application} />
        {/* ADDITIVE (v1.9.24, T.Z111) — the enrollment fact, a second chip for the
            second axis: the contract document and the enrollment decision stay two
            separately-named facts. Rendered only when the sync has reported one — no
            fact is no chip, never a guessed «не зачислен». */}
        {application.enrollState !== null && <EnrollChip state={application.enrollState} />}
        <span className="source-badge" data-testid="profile-venue-match" data-match={application.venueMatchStatus}>
          {VENUE_MATCH_LABEL[application.venueMatchStatus]}
        </span>
      </header>
      <p className="profile__subtitle" data-testid="profile-updated">
        Обновлено {formatDateTime(application.updatedAt)} · в системе с {formatDate(application.firstSeenAt)}
      </p>

      {/* ADDITIVE (v1.8.0, Р9/D8, T.Y10) — the journal-backed «Статус менеджера». */}
      <ManagerStatusSection pguId={pguId} canSet={canRepair} />

      <section className="profile__section" aria-label="Поля заявления">
        <h3 className="profile__section-title">Поля и источники</h3>
        <div className="profile__grid">
          <ProfileField field="untiId" label="УНТИ ID" source="FTP">
            {application.untiId ?? MISSING}
          </ProfileField>
          <ProfileField field="courseId" label="Курс" source="EPGU">
            {courseLabel(application.courseId)}
          </ProfileField>
          <ProfileField field="region" label="Регион" source="EPGU">
            {application.region ?? MISSING}
          </ProfileField>
          <ProfileField field="epguStatusCode" label="Статус ЕПГУ (сырой)" source="EPGU" wide>
            {application.epguStatusCode ?? MISSING}
          </ProfileField>
          <ProfileField field="fullName" label="ФИО" source="EVI">
            {application.fullName ?? MISSING}
          </ProfileField>
          <ProfileField field="phone" label="Телефон" source="EVI">
            {application.phone ?? MISSING}
          </ProfileField>
          <ProfileField field="email" label="Email" source="EVI">
            {application.email ?? MISSING}
          </ProfileField>
          <ProfileField field="venueName" label="Площадка" source="MATCHER">
            {application.venueName ?? MISSING}
            {application.venueId !== null ? (
              <span className="profile__subtitle"> · id {application.venueId}</span>
            ) : null}
          </ProfileField>
          {/* ADDITIVE (v1.8.3, T.Z21) — right under the venue it explains: the ЕВИ
              address the matcher read, red while the venue is missing. */}
          <SnapshotAddressRow application={application} />
          {/* ADDITIVE (v1.9.5, T.Z52) — «Группа» / its педагог, for ADMIN/МЕНЕДЖЕР
              only (the api leaves the key off every other role, so the block does not
              render for them at all). Read-only: a group is written from the curator
              cabinet, never from the card. */}
          <StudentGroupsRow application={application} />
          <ProfileField field="linkReceivedAt" label="Ссылка отправлена" source="EVI">
            {formatDateTime(application.linkReceivedAt)}
          </ProfileField>
          <ProfileField field="eviPassedAt" label="ЕВИ пройдено" source="FTP">
            {formatDateTime(application.eviPassedAt)}
          </ProfileField>
        </div>
      </section>

      {/* T.F1 — the whole ЕПГУ row, grouped the way a manager reads it. */}
      <section className="profile__section" aria-label="Данные из ЕПГУ">
        <h3 className="profile__section-title">Данные из ЕПГУ</h3>
        {profileFieldGroups(application).map((group) => (
          <div className="profile__group" key={group.key} data-testid={`profile-group-${group.key}`}>
            <h4 className="profile__group-title">{group.title}</h4>
            <div className="profile__grid">
              {group.fields.map((entry) => (
                <ProfileField key={entry.field} field={entry.field} label={entry.label} source={entry.source}>
                  {entry.text ?? MISSING}
                </ProfileField>
              ))}
            </div>
          </div>
        ))}
      </section>

      {canRepair ? (
        <RepairPanel application={application} patch={patch} />
      ) : (
        <p className="profile__panel-hint" data-testid="repair-locked">
          Ручные правки доступны ролям не ниже менеджера.
        </p>
      )}

      <section className="profile__section" aria-label="История изменений">
        <h3 className="profile__section-title">
          История изменений <span data-testid="timeline-count">{application.events.length}</span>
        </h3>
        {application.events.length === 0 ? (
          <EmptyState title="Событий пока нет" hint="Первое событие появится со следующим снапшотом." />
        ) : (
          <ul className="timeline" data-testid="timeline">
            {timelineOf(application.events).map((event) => (
              <li className="timeline__row" key={event.key} data-testid="timeline-row" data-type={event.type}>
                <span className="timeline__when">{formatDateTime(event.createdAt)}</span>
                <span className="timeline__body">
                  <span className="timeline__head">
                    <Badge tone={event.type === 'STATUS_CHANGED' ? 'brand' : 'neutral'}>{event.typeLabel}</Badge>
                    <span className="source-badge" data-source={event.source}>
                      {event.sourceLabel}
                    </span>
                    <span className="cell-muted">
                      {event.actorLabel}
                      {event.fieldLabel ? ` · ${event.fieldLabel}` : ''}
                    </span>
                  </span>
                  <span
                    className={event.change === null ? 'timeline__change timeline__change--empty' : 'timeline__change'}
                    data-testid="timeline-change"
                  >
                    {event.change ?? '—'}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * «Статус менеджера» (Р9/D8, SR v2.1 §3.6/§3.7 — T.Y10).
 *
 * The base keeps a JOURNAL (#5.5 — rows are never deleted) and this block shows the
 * ONE value the display rule picks: the active row with the greatest `assignedAt`
 * (A9 as amended by D8). No active row is the «без статуса» state, not an error.
 *
 * MANAGER+ changes it through #5.6, a REPLACE-SET: the combobox over the ACTIVE half
 * of the dictionary (#28) commits `[id]`, the clear button and «Снять статус» commit
 * `[]`. The write is optimistic — the chosen status lands before the request does,
 * and a 400 (unknown or inactive id, e.g. the dictionary changed under the open
 * drawer) rolls the value back with the api's message. Audit and the history halves
 * are the server's business.
 *
 * The closed assignments surface only as the «история: N смен» counter — the full
 * journal UI is deliberately not this wave.
 */
function ManagerStatusSection({ pguId, canSet }: { pguId: string; canSet: boolean }) {
  const journal = useApplicationManagerStatuses(pguId);
  const dictionary = useManagerStatusDictionary();
  const put = useSetApplicationManagerStatuses(pguId);
  const block = managerStatusBlock({ journal: journal.data, dictionary: dictionary.data });

  /** One combobox commit → one #5.6 body; a no-op never leaves the browser. */
  const apply = (value: string) => {
    const intent = managerStatusPut(value, block.active);
    if (intent.changed) put.mutate(intent.statusIds);
  };

  return (
    <section className="profile__section" aria-label="Статус менеджера" data-testid="manager-status-block">
      <h3 className="profile__section-title">
        Статус менеджера
        {block.historyLabel ? (
          <span className="cell-muted" data-testid="manager-status-history" title="Сколько раз статус назначали и снимали">
            {block.historyLabel}
          </span>
        ) : null}
      </h3>

      {journal.isPending ? (
        <p className="profile__panel-hint" data-testid="manager-status-loading">
          Загружаем статусы…
        </p>
      ) : journal.isError ? (
        <ErrorState
          message={
            journal.error instanceof Error ? journal.error.message : 'Не удалось загрузить статусы менеджера.'
          }
          onRetry={() => void journal.refetch()}
        />
      ) : (
        <div className="manager-status">
          <div className="profile__row">
            <span
              className={block.hasStatus ? undefined : 'cell-muted'}
              data-testid="manager-status-value"
              data-has-status={block.hasStatus ? 'true' : 'false'}
            >
              {block.hasStatus ? (
                <Badge tone="brand" title={block.active ? `Назначено ${formatDateTime(block.active.assignedAt)}` : undefined}>
                  {block.label}
                </Badge>
              ) : (
                NO_STATUS_LABEL
              )}
            </span>

            {canSet ? (
              <>
                <Combobox
                  testId="manager-status-select"
                  value={block.value}
                  options={block.options}
                  onChange={apply}
                  placeholder={block.hasStatus ? 'Заменить статус' : 'Выбрать статус'}
                  emptyText="Статус не найден"
                  clearLabel="Снять статус"
                  ariaLabel="Статус менеджера"
                  resultsLabel={(count) => `Найдено статусов: ${count}`}
                  disabled={put.isPending}
                />
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  data-testid="manager-status-clear"
                  disabled={!block.hasStatus || put.isPending}
                  onClick={() => apply('')}
                >
                  Снять статус
                </button>
              </>
            ) : null}
          </div>

          <p className="profile__panel-hint">
            {canSet
              ? 'Словарь статусов ведёт администратор в настройках; каждая смена — в истории и аудите.'
              : 'Назначение статуса доступно ролям не ниже менеджера.'}
          </p>

          {put.isError ? (
            <p className="profile__error" role="alert" data-testid="manager-status-error">
              {managerStatusErrorText(
                put.error instanceof ApiError ? put.error : undefined,
                put.error instanceof Error ? put.error.message : undefined,
              ) ?? 'Не удалось сохранить статус менеджера.'}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}

/** One read-only field: its label, the owner badge and the value as the api sent it. */
function ProfileField({
  field,
  label,
  source,
  wide,
  valueClass,
  children,
}: {
  field: string;
  label: string;
  source: FieldSource;
  wide?: boolean;
  /** ADDITIVE (v1.8.3, T.Z21) — an extra class on the value (the red unlinked state). */
  valueClass?: string;
  children: ReactNode;
}) {
  return (
    <div className={wide ? 'profile__field profile__field--wide' : 'profile__field'}>
      <span className="profile__field-label">
        {label}
        <span className="source-badge" data-source={source} title={`Источник: ${SOURCE_LABEL[source]}`}>
          {SOURCE_LABEL[source]}
        </span>
      </span>
      <span
        className={valueClass ? `profile__field-value ${valueClass}` : 'profile__field-value'}
        data-testid={`profile-${field}`}
      >
        {children}
      </span>
    </div>
  );
}

/**
 * «Адрес (из выгрузки)» (ADDITIVE v1.8.3, T.Z21) — the ЕВИ snapshot address the venue
 * matcher read, on the card so an UNMATCHED row shows the evidence that failed to
 * match: RED while `venueId` is null (with «не сопоставлен с площадкой» underneath),
 * quiet grey once linked. `address` absent/null hides the row — no evidence is a
 * state, not an empty field. Hidden rather than «—» on purpose: an empty red box
 * would read as a rendering bug.
 */
function SnapshotAddressRow({ application }: { application: ApplicationDetailDto }) {
  const view = snapshotAddressView(application);
  if (view === null) return null;
  return (
    <ProfileField
      field="snapshotAddress"
      label={SNAPSHOT_ADDRESS_LABEL}
      source="EVI"
      valueClass={view.tone === 'unlinked' ? 'profile__field-value--unlinked' : 'profile__field-value--muted'}
    >
      {view.text}
      {view.hint ? (
        <span className="profile__field-hint" data-testid="profile-snapshot-address-hint">
          {view.hint}
        </span>
      ) : null}
    </ProfileField>
  );
}

/**
 * «Группа» (ADDITIVE v1.9.5, T.Z52) — the study groups this child belongs to and their
 * педагоги. STAFF-ONLY by the api's own rule: `groups` is set for an ADMIN/МЕНЕДЖЕР
 * session and absent for everyone else, so `application.groups === undefined` renders
 * nothing at all — an absent key and an empty roster never read the same. The row sits
 * right under the venue it refines: the площадка says WHERE the child studies, the
 * group says IN WHICH of its groups and with which педагог.
 */
function StudentGroupsRow({ application }: { application: ApplicationDetailDto }) {
  const groups = application.groups;
  if (groups === undefined) return null;
  return (
    <ProfileField field="studyGroup" label="Группа" source="MANUAL" wide>
      {groups.length === 0 ? (
        <span data-testid="profile-study-group-none">Группа не назначена</span>
      ) : (
        <span data-testid="profile-study-group">
          {groups.map((group) => `${group.name}${group.teacherName !== null ? ` — пед. ${group.teacherName}` : ''}`).join('; ')}
        </span>
      )}
    </ProfileField>
  );
}

function FlagBadge({ label, on, testId }: { label: string; on: boolean; testId: string }) {
  if (!on) return null;
  return (
    <Badge tone="warn" testId={testId}>
      {label}
    </Badge>
  );
}

/**
 * T.Z113 — the profile's «Провалил ЕВИ» chip: the shared fold of the AUTO fact
 * (`evi_failed`, migration 0031) and the MANUAL one (`evi_failed_manually`, T.Z96),
 * one chip for either source. `null` = neither fact holds, render nothing — the same
 * contract `FlagBadge` follows, so the card's chrome is unchanged.
 */
function EviFailedChip({
  eviFailed,
  eviFailedManually,
}: {
  eviFailed: boolean;
  eviFailedManually: boolean;
}) {
  const chip = eviFailedChip({ eviFailed, eviFailedManually });
  if (chip === null) return null;
  return <FlagBadge label={chip.label} on={chip.on} testId="profile-flag-evi-failed" />;
}

/**
 * «Дедлайн ЕВИ» (T.F1): `linkReceivedAt + 10 дней`, chip in the card header.
 * Rendered only while the clock is running (LINK_RECEIVED with a link date) — the
 * profile of a passed / rejected application has nothing left to count down to.
 * Overdue turns the chip red (`data-overdue`, the same signal the table uses).
 */
function EviDeadlineChip({ application }: { application: ApplicationDetailDto }) {
  const deadline = eviDeadline(application);
  if (deadline === null) return null;
  return (
    <span
      data-testid="profile-evi-deadline"
      className={deadline.overdue ? 'deadline-chip deadline-chip--overdue' : 'deadline-chip'}
      data-overdue={deadline.overdue ? 'true' : 'false'}
      title={`Ссылка отправлена + ${EVI_DEADLINE_DAYS} дней = ${formatDateTime(deadline.at)}`}
    >
      Дедлайн ЕВИ · {deadline.label}
    </span>
  );
}

/**
 * The MANAGER+ half of contract #5: the two manual flags, the venue override and
 * the PII null-fill form. Optimistic — the toggle answers before the request does,
 * and a 403 rolls the row back with the api's message.
 *
 * Wave 9 (T.Y9) guards the venue row (Р7 v2.1 / A8), AMENDED 14.09 (v1.8.8): an
 * OFFLINE application (or one with no format) receives a venue ONCE — an
 * UNASSIGNED row shows the ADMIN the same editable control with the confirmation
 * step an online row gets, and a non-ADMIN reads it DISABLED with the role hint;
 * a row that already HAS a venue never moves again, with its own hint for every
 * role. An ONLINE row moves only by an ADMIN (the partner branches are shared
 * capacity, and a fat-fingered id would rewrite an attribution).
 *
 * Wave 10 (T.Z16) replaces the raw-id input with a type-ahead over the venue
 * directory (#6) — a venue is named, never typed as a number — and names the
 * unlink «Убрать площадку», a destructive action with its own armed state.
 * A MANAGER on an ONLINE row reads the same control DISABLED with the role hint,
 * so the permission boundary is visible instead of implied.
 */
function RepairPanel({
  application,
  patch,
}: {
  application: ApplicationDetailDto;
  patch: ReturnType<typeof usePatchApplication>;
}) {
  const { role } = useAuth();
  const [piiDraft, setPiiDraft] = useState<PiiFillDraft>(emptyPiiFillDraft);
  // The pending combobox selection — `''` is "nothing chosen yet", never an unlink.
  const [venueDraft, setVenueDraft] = useState('');
  const [venueError, setVenueError] = useState<string | undefined>(undefined);
  const [venueConfirm, setVenueConfirm] = useState<VenueConfirmIntent | null>(null);

  const venueState = venueControlState(application, role);
  const directory = useVenueDirectory();

  // A successful repair clears the drafts; a failed one keeps them so the
  // manager can fix the value instead of retyping everything.
  useEffect(() => {
    if (patch.isSuccess) {
      setPiiDraft(emptyPiiFillDraft());
      setVenueDraft('');
      setVenueError(undefined);
      setVenueConfirm(null);
    }
  }, [patch.isSuccess, patch.data]);

  const pii = piiFillPatch(piiDraft, application);
  const venue = venueAssignPatch(application, venueDraft);
  const remove = venueRemovePatch(application);
  const repairing = patch.isPending;

  const onVenueChange = (value: string) => {
    setVenueDraft(value);
    setVenueError(venueAssignError(value));
    setVenueConfirm(null);
  };

  /**
   * The confirm gate (T.Y9, kept): the first click arms the intent, the second
   * one sends it. Arming is per intent — switching from «Назначить» to «Убрать
   * площадку» never inherits the confirmation of the other.
   */
  const requestVenuePatch = (intent: VenueConfirmIntent, body: ApplicationPatchInput) => {
    if (venueConfirm !== intent) {
      setVenueConfirm(intent);
      return;
    }
    patch.mutate(body);
  };

  // The searchable list. A venue the directory does not know any more (renamed,
  // retired) is injected from the row's own name, so the current selection always
  // resolves to a label — the same rescue the filter bar applies.
  const venueOptions = [
    ...venueAssignFallbackOption(application),
    ...venueAssignOptions(directory.data ?? []),
  ];

  return (
    <section className="profile__section" aria-label="Ручные правки">
      <h3 className="profile__section-title">Ручные правки</h3>
      <div className="profile__panel">
        <p className="profile__panel-hint">
          Правки пишутся в историю изменений и в журнал аудита. Статус и поля, заполненные
          источником, вручную не меняются.
        </p>

        {FLAG_ROWS.map(({ field, label }) => (
          <div className="profile__row" key={field} data-testid={`flag-row-${field}`}>
            <span className="profile__row-label">{label}</span>
            <span className="source-badge">{SOURCE_LABEL[FIELD_SOURCE[field] ?? 'MANUAL']}</span>
            <span data-testid={`flag-value-${field}`} className="profile__field-value">
              {boolLabel(application[field])}
            </span>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              data-testid={`flag-toggle-${field}`}
              aria-pressed={application[field]}
              disabled={repairing}
              onClick={() => patch.mutate(flagTogglePatch(application, field))}
            >
              {application[field] ? 'Снять' : 'Поставить'}
            </button>
          </div>
        ))}

        <div
          className="profile__row"
          data-testid="venue-assign"
          data-venue-state={venueState}
        >
          <span className="profile__row-label">Площадка</span>
          <span className="profile__field-value" data-testid="venue-assign-current">
            {application.venueName ?? VENUE_EMPTY_LABEL}
            {application.venueId !== null ? ` · id ${application.venueId}` : ''}
          </span>
          {/* Р7 v2.1 (SR §A8): for an OFFLINE row the control stays VISIBLE and
              DISABLED — «задизейблено… не скрыто» — so a curator/manager reads the
              silence as a rule, not as a rendering bug; the api refuses the PATCH
              anyway (`VENUE_CHANGE_OFFLINE_FORBIDDEN`, ADMIN included). T.Z16: the
              same read-only treatment carries the role boundary for a MANAGER on an
              ONLINE row — the venue is on the screen, the decision is not theirs. */}
          {/* The combobox holds the PENDING choice, not the row's state — the row's
              venue is the read-only text above («venueName · id N» / «Без площадки»).
              That keeps the × honest: it exists only while a choice is pending, and
              undoing a choice is never one click away from unlinking the venue. */}
          <Combobox
            testId="venue-assign-combobox"
            value={venueDraft}
            options={venueOptions}
            onChange={onVenueChange}
            placeholder={
              venueState === 'editable'
                ? application.venueId === null
                  ? 'Без площадки — найдите по названию'
                  : 'Заменить — найдите по названию'
                : undefined
            }
            emptyText="Площадка не найдена"
            clearLabel="Отменить выбор"
            ariaLabel="Площадка"
            resultsLabel={(count) => `Найдено площадок: ${count}`}
            disabled={repairing || venueState !== 'editable'}
          />
          {/* The buttons follow the wave-9 shape: visible-but-dead on an OFFLINE row
              («задизейблено… не скрыто»), absent where the ROLE is the reason — a
              manager on an ONLINE row reads the hint, not controls he may not use. */}
          {venueState !== 'admin-only' ? (
            <>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                data-testid="venue-assign-apply"
                disabled={venueState !== 'editable' || !venue.canSubmit || repairing}
                onClick={() => requestVenuePatch('assign', venue.patch)}
              >
                {venueConfirm === 'assign' ? VENUE_ASSIGN_CONFIRM_LABEL : VENUE_ASSIGN_LABEL}
              </button>
              <button
                type="button"
                className="btn btn--danger btn--sm"
                data-testid="venue-assign-remove"
                disabled={venueState !== 'editable' || !remove.canSubmit || repairing}
                onClick={() => requestVenuePatch('remove', remove.patch)}
              >
                {venueConfirm === 'remove' ? VENUE_REMOVE_CONFIRM_LABEL : VENUE_REMOVE_LABEL}
              </button>
            </>
          ) : null}
          {venueState === 'offline-locked' ? (
            <p className="profile__hint" data-testid="venue-assign-hint">
              {VENUE_OFFLINE_HINT}
            </p>
          ) : null}
          {venueState === 'admin-only' ? (
            <p className="profile__hint" data-testid="venue-assign-hint">
              {/* A8 AMENDED 14.09 (v1.8.8): an unassigned OFFLINE row is the ADMIN's
                  first assignment, so the manager's hint names that, not the online rule.
                  T.Z245: «offline vs online» is the served EFFECTIVE format — the same
                  value the gate above judged and the api's guard judges. */}
              {application.format === 'ONLINE' ? VENUE_ROLE_HINT : VENUE_OFFLINE_FIRST_HINT}
            </p>
          ) : null}
          {/* The searchable list IS the control now — a directory that never answered
              must say so, or the empty list reads as «no venues exist». */}
          {venueState === 'editable' && directory.isError ? (
            <p className="profile__hint" data-testid="venue-assign-directory-error">
              Справочник площадок не загрузился — поиск недоступен.{' '}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                data-testid="venue-assign-directory-retry"
                onClick={() => void directory.refetch()}
              >
                Повторить
              </button>
            </p>
          ) : null}
          {venueConfirm === 'assign' ? (
            <p className="profile__hint" data-testid="venue-assign-confirm-hint">
              Смена площадки пишется в историю изменений и журнал аудита.
            </p>
          ) : null}
          {venueConfirm === 'remove' ? (
            <p className="profile__hint" data-testid="venue-assign-confirm-hint">
              Площадка будет снята, заявление вернётся в «Не сопоставлено» — правка пишется в
              историю изменений и журнал аудита.
            </p>
          ) : null}
          {venueError ? (
            <p className="profile__error" role="alert" data-testid="venue-assign-error">
              {venueError}
            </p>
          ) : null}
        </div>

        <form
          className="profile__panel"
          data-testid="pii-fill"
          onSubmit={(event) => {
            event.preventDefault();
            if (pii.canSubmit) patch.mutate(pii.patch);
          }}
        >
          <p className="profile__panel-hint">
            Пустые поля ФИО / телефона / email можно заполнить вручную. Заполненные источником
            (ЕВИ) поля править нельзя — только они имеют приоритет над ручным вводом.
          </p>
          {PII_ROWS.map(({ field, label, placeholder }) => {
            const state = piiFillState(field, piiDraft, application);
            return (
              <div className="profile__row" key={field} data-testid={`pii-row-${field}`}>
                <span className="profile__row-label">{label}</span>
                {state.editable ? (
                  <Input
                    data-testid={`pii-fill-${field}`}
                    aria-label={label}
                    placeholder={placeholder}
                    value={piiDraft[field]}
                    aria-invalid={state.error !== undefined}
                    onChange={(event) => setPiiDraft({ ...piiDraft, [field]: event.currentTarget.value })}
                  />
                ) : (
                  <span className="profile__field-value profile__field-value--muted" data-testid={`pii-owned-${field}`}>
                    {application[field] ?? MISSING} · источник: ЕВИ
                  </span>
                )}
                {state.error ? (
                  <p className="profile__error" role="alert" data-testid={`pii-error-${field}`}>
                    {state.error}
                  </p>
                ) : null}
              </div>
            );
          })}
          <div className="profile__panel-actions">
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              data-testid="pii-fill-submit"
              disabled={!pii.canSubmit || repairing}
            >
              Заполнить
            </button>
            <span className="profile__panel-hint">
              {pii.sourceOwned.length > 0
                ? `Источник владеет: ${pii.sourceOwned.map((field) => FIELD_LABEL[field]).join(', ')}.`
                : 'Все три поля пусты.'}
            </span>
          </div>
        </form>

        {patch.isError ? (
          <p className="profile__error" role="alert" data-testid="patch-error">
            {patch.error instanceof Error ? patch.error.message : 'Не удалось сохранить правку.'}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/** The manual flags of contract #5, in the order the api documents them. */
const FLAG_ROWS: ReadonlyArray<{ field: FlagField; label: string }> = [
  { field: 'isOrganic', label: 'Органик (пришёл сам, без ЕПГУ)' },
  { field: 'agreementRejected', label: 'Отказ от договора' },
  // ADDITIVE (v1.9.15, T.Z96) — the «не прошёл ЕВИ» fallback: the toggle ON states the
  // fact, the toggle OFF takes it back; both writes land in the journal and the audit.
  { field: 'eviFailedManually', label: 'Не прошёл ЕВИ (вручную)' },
];

const PII_ROWS: ReadonlyArray<{
  field: 'fullName' | 'phone' | 'email';
  label: string;
  placeholder: string;
}> = [
  { field: 'fullName', label: 'ФИО', placeholder: 'Фамилия…' },
  { field: 'phone', label: 'Телефон', placeholder: '+7(XXX)XXXXXXX' },
  { field: 'email', label: 'Email', placeholder: 'ivanov.i@example.com' },
];
